import { z } from 'zod';
import { isoDate } from './common.js';

/**
 * Схемы адресов площадок (007): `specs/007-sites/contracts/http-api.md`, data-model.md А.2–А.10, research R19.
 * Маршруты `/api/sites/…` проверяют вход этими схемами, интерфейс читает ответы по ним. Все объекты — `strictObject`:
 * лишнее поле — ошибка. Секретов в ответах нет: пароль SmartPSS — только `GET …/secret` (`SiteSecret`), файл
 * подключения VPN — только `GET …/vpn-config`. ФИО в схемах 007 нет.
 */

const S = z.strictObject;
const id = z.number().int().positive();
const count = z.number().int().nonnegative();
const revision = z.number().int().min(1);
/** Время — ISO 8601 с поясом (`…Z` или `+05:00`). */
const isoTime = z.iso.datetime({ offset: true });
const text = (min: number, max: number) => z.string().trim().min(min).max(max);
const json = z.record(z.string(), z.unknown());

// ── Площадка ──

/** Код площадки = id объекта: латиница, цифры, `-`, 2–20 знаков, первая — буква (data-model А.2). */
export const SiteCode = z.string().regex(/^[a-z][a-z0-9-]{1,19}$/, 'Код: латиница, цифры, «-», 2–20 знаков');
export type SiteCode = z.infer<typeof SiteCode>;

/** Пояс объекта, как `objects.tz_offset`: `+05:00`. */
export const TzOffset = z.string().regex(/^[+-]\d{2}:\d{2}$/, 'Пояс в формате +ЧЧ:ММ');

/** Способ связи (шаг 3): напрямую через интернет или через VPN. */
export const LinkMode = z.enum(['direct', 'vpn']);
export type LinkMode = z.infer<typeof LinkMode>;

/** Состояние площадки (data-model А.2): переходы — там же. */
export const SiteState = z.enum(['onboarding', 'observing', 'active', 'no_link', 'disabled']);
export type SiteState = z.infer<typeof SiteState>;

/** Направление терминала (data-model А.4): вход, выход, не учитывать. */
export const Direction = z.enum(['IN', 'OUT', 'SKIP']);
export type Direction = z.infer<typeof Direction>;

/** Адрес IPv4: терминал, адрес площадки в интернете, адрес в VPN. */
export const Ipv4 = z.ipv4();

/** Время «ЧЧ:ММ» по поясу площадки (выгрузка по расписанию). */
export const LocalTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Время в формате ЧЧ:ММ');

// ── Шаги ──

export const StepNo = z.number().int().min(1).max(10);
export type StepNo = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

/** Статус шага (data-model А.3): кружок шага в `Steps`. */
export const StepStatus = z.enum(['todo', 'current', 'wait', 'ok', 'bad', 'warn']);
export type StepStatus = z.infer<typeof StepStatus>;

/**
 * Коды состояний шага (research R19): у каждого шага есть код, в том числе у `ok` и `wait` — по нему интерфейс берёт
 * строку состояния (`@tabel/shared/sites/step-texts`). Имена — как в `api/src/modules/sites/checks.ts`.
 */
export const STEP_STATE_CODES = [
  'PREPARING',
  'READY',
  'ANSWERS_TODO',
  'LINK_DIRECT',
  'VPN_WAIT',
  'VPN_OK',
  'LOGIN_WAIT',
  'LOGGED_IN',
  'LINK_OK',
  'TERMINALS_WAIT',
  'TERMINALS_CHOOSE',
  'TERMINALS_OK',
  'CTL_WAIT',
  'CTL_OK',
  'CTL_SKIPPED',
  'EXTRACT_TODO',
  'EXTRACT_WAIT',
  'EXTRACT_OK',
  'EXTRACT_MANUAL',
  'PEOPLE_NONE',
  'PEOPLE_OK',
  'OBSERVING',
  'OBSERVED',
  'LAUNCH_READY',
  'LAUNCHED',
  'LOCKED',
] as const;

/** Коды ошибок (`bad`) и рисков (`warn`) шагов (research R19, data-model А.3). */
export const STEP_PROBLEM_CODES = [
  'PREPARE_FAILED',
  'VERSION_UNKNOWN',
  'RETENTION_SHORT',
  'PC_NOT_24H',
  'VPN_SILENT',
  'VPN_BLOCKED',
  'LINK_FAILED',
  'PASS_MISMATCH',
  'USER_NAME',
  'DB_NAME',
  'NO_ATTEMPTS',
  'NO_TABLE',
  'NEW_ADDRESS',
  'TERMINALS_MISSING',
  'NO_DIRECTION',
  'DIRECTION_SWAPPED',
  'CLOCK_SKEW',
  'CTL_NOT_FOUND',
  'CTL_WRONG_TERMINAL',
  'CTL_DELAY',
  'CTL_CLOCK',
  'EXTRACT_MISSED',
  'OBS_DELETES',
  'OBS_CODES',
] as const;

export const StepCode = z.enum([...STEP_STATE_CODES, ...STEP_PROBLEM_CODES]);
export type StepCode = z.infer<typeof StepCode>;

/** Ошибка терминала в шаге 5 (одна на терминал). */
export const TerminalProblem = StepCode.extract(['NO_DIRECTION', 'DIRECTION_SWAPPED', 'CLOCK_SKEW']);
export type TerminalProblem = z.infer<typeof TerminalProblem>;

/** Принятый риск шага с `warn` (FR-015): причина, кто, когда. */
export const StepRisk = S({ reason: z.string(), by: id, at: isoTime });

/**
 * Шаг подключения: номер, статус, код, детали для текста (числа и адреса, research R19), ответы шагов 2, 3, 6, 7 и
 * принятый риск. Детали и ответы — объекты: их поля задаёт код (R19, `checks.ts`).
 */
export const SiteStep = S({
  step: StepNo,
  status: StepStatus,
  code: StepCode,
  details: json,
  answers: json.nullable(),
  risk: StepRisk.nullable(),
  updatedAt: isoTime,
});
export type SiteStep = z.infer<typeof SiteStep>;

// ── Ответы шагов ──

/** Шаг 2 (FR-018): версия необязательна; пропуск нормализуется в пустую строку. */
export const Step2Answers = S({
  version: text(0, 40).default(''),
  terminals: z.number().int().min(1).max(100),
  allDay: z.boolean(),
  keepDays: z.number().int().min(0).max(3650),
});
/** Шаг 3: администратор подтверждает, что в WireGuard статус «Активен», а сигнала нет (research R19). */
export const Step3Answers = S({ vpnActive: z.boolean() });
/** Шаг 5: явное подтверждение направлений, выбранных вручную вместо направления SmartPSS. */
export const Step5Answers = S({ manualDirectionsConfirmed: z.literal(true) });
/** Шаг 7 «Выгрузка по расписанию» (FR-024): «настроено» и назначенные времена; по умолчанию одно — 08:00. */
export const Step7Answers = S({ times: z.array(LocalTime).min(1).max(3) });

// ── Терминалы, наблюдение, загрузки, тревоги ──

/** Терминал площадки в шаге 5: адрес, имя в SmartPSS, выбранное направление с датой, отметки, проверка. */
export const TerminalRow = S({
  ip: Ipv4,
  deviceName: z.string().nullable(),
  /** Выбранное направление на сегодня; не выбрано — `null`. */
  direction: Direction.nullable(),
  validFrom: isoDate.nullable(),
  count,
  lastPassAt: isoTime.nullable(),
  problem: TerminalProblem.nullable(),
  /** Расхождение часов терминала, мин: меньше нуля — часы спешат (R19, `source_ts_ms − attendance_ms`). */
  skewMinutes: z.number().int().nullable(),
});
export type TerminalRow = z.infer<typeof TerminalRow>;

/** Итог дня наблюдения (FR-026): «20.10: 388 отметок, ночью тишина 22:40–05:50, выгрузка 08:00 пришла». */
export const ObservationDay = S({
  date: isoDate,
  passes: count,
  quiet: z.tuple([LocalTime, LocalTime]).nullable(),
  extracts: z.array(S({ at: LocalTime, arrived: z.boolean() })),
});
/** Итоги наблюдения (шаг 9): начало и итог каждого дня. Пункты — в деталях шага 9. */
export const Observation = S({ startedAt: isoTime.nullable(), days: z.array(ObservationDay) });

export const UploadKind = z.enum(['people', 'records']);
export type UploadKind = z.infer<typeof UploadKind>;
/** Причина отказа строки выгрузки — кодом, без ФИО (data-model А.6). */
export const UploadRejectReason = z.enum(['no_person', 'no_name', 'bad_time', 'no_device']);
const RejectedRow = S({ row: z.number().int().min(1), reason: UploadRejectReason });

/** Загрузка выгрузки SmartPSS (data-model А.6): итоги, без файла и без ФИО. */
export const SiteUpload = S({
  id,
  kind: UploadKind,
  fileName: z.string(),
  periodFrom: isoDate.nullable(),
  periodTo: isoDate.nullable(),
  rowsTotal: count,
  added: count,
  duplicates: count,
  rejected: count,
  rejectedRows: z.array(RejectedRow),
  rejectedByReason: z.partialRecord(UploadRejectReason, count).default({}),
  by: id,
  at: isoTime,
});
export type SiteUpload = z.infer<typeof SiteUpload>;

/** Предпросмотр загрузки (`dryRun=1`): строки, распознанные колонки, отклонённые с причинами, период. */
export const UploadPreview = S({
  kind: UploadKind,
  fileName: z.string(),
  rowsTotal: count,
  recognized: count,
  /** Заголовок файла, найденный для каждой колонки (синонимы research R10). */
  columns: z.partialRecord(z.enum(['person', 'name', 'time', 'date', 'device', 'state']), z.string()),
  rejected: count,
  rejectedRows: z.array(RejectedRow),
  period: S({ from: isoDate, to: isoDate }).nullable(),
  timeRange: S({ first: isoTime, last: isoTime }).nullable().default(null),
  warnings: z.array(z.string()).default([]),
});
export type UploadPreview = z.infer<typeof UploadPreview>;

/** Вид тревоги — как в миграции 0012 (data-model А.8 и `auth_failures`). */
export const SiteAlertKind = z.enum([
  'pc_offline',
  'smartpss_silent',
  'extract_missed',
  'new_address',
  'unknown_codes',
  'schema_changed',
  'deletes',
  'clock_skew',
  'quarantine',
  'auth_failures',
]);
export type SiteAlertKind = z.infer<typeof SiteAlertKind>;

/** Тревога площадки (data-model А.8). Одна открытая на вид. */
export const SiteAlert = S({
  id,
  kind: SiteAlertKind,
  openedAt: isoTime,
  closedAt: isoTime.nullable(),
  details: json,
});
export type SiteAlert = z.infer<typeof SiteAlert>;

export const AlertChannel = z.enum(['ntfy', 'inapp']);
/** Кому сообщать о тревогах площадки (data-model А.9): тема ntfy или пользователь сервиса. */
export const AlertRecipient = S({ channel: AlertChannel, target: text(1, 200) });
export type AlertRecipient = z.infer<typeof AlertRecipient>;
export const AlertRecipientRow = S({
  channel: AlertChannel,
  target: z.string(),
  addedBy: id,
  addedAt: isoTime,
});

/** Тревоги площадки (`GET …/alerts`): открытые и последние закрытые, новые сверху, и кому о них сообщать. */
export const SiteAlertsView = S({ alerts: z.array(SiteAlert), recipients: z.array(AlertRecipientRow) });
export type SiteAlertsView = z.infer<typeof SiteAlertsView>;

/** Строка карантина (data-model А.10): номер строки копии, причина, время. Без ФИО. */
export const QuarantineRow = S({
  sourceChangeId: id,
  reason: z.enum(['unknown_site', 'src_mismatch', 'unknown_code', 'new_columns']),
  seenAt: isoTime,
  releasedAt: isoTime.nullable(),
});
export type QuarantineRow = z.infer<typeof QuarantineRow>;

/** Итог выпуска строк карантина (`POST …/quarantine/release`): сколько строк выпущено в обработку. */
export const QuarantineReleased = S({ released: count, imported: count });
export type QuarantineReleased = z.infer<typeof QuarantineReleased>;

/**
 * Подсказка периода ремонта (`GET …/repair-hint`): периоды, когда отметки могли не прийти. Причина — кодом: тревоги
 * `pc_offline`, `smartpss_silent`, `extract_missed` и `ingest_error` (импорт не читает копию отметок).
 */
export const RepairReason = z.enum(['pc_offline', 'smartpss_silent', 'extract_missed', 'ingest_error']);
export const RepairHint = S({
  periods: z.array(S({ from: isoTime, to: isoTime, reason: RepairReason })),
});
export type RepairHint = z.infer<typeof RepairHint>;

// ── Ответы адресов ──

/** Строка списка «Настройки → Площадки» (`GET /api/sites`, FR-002). */
export const SiteListItem = S({
  code: SiteCode,
  name: z.string(),
  city: z.string(),
  tzOffset: TzOffset,
  linkMode: LinkMode,
  /** Пометка «без шифрования» — способ «напрямую» (поправка конституции 19.10). */
  unencrypted: z.boolean(),
  terminals: count,
  lastPassAt: isoTime.nullable(),
  state: SiteState,
  /** Для «Подключение · шаг N из 10». */
  currentStep: StepNo.nullable(),
  /** Для «Наблюдение · день N из 3». */
  observationDay: z.number().int().min(1).nullable(),
});
export type SiteListItem = z.infer<typeof SiteListItem>;

/** Площадка в `SiteDetail` (data-model А.2, `objects`). */
export const SiteInfo = S({
  code: SiteCode,
  name: z.string(),
  city: z.string(),
  tzOffset: TzOffset,
  linkMode: LinkMode,
  state: SiteState,
  dbName: z.string(),
  smartpssUser: z.string(),
  vpnAddress: Ipv4.nullable(),
  boundAddress: Ipv4.nullable(),
  startDate: isoDate.nullable(),
  rulesSource: SiteCode.nullable(),
  createdAt: isoTime,
  launchedAt: isoTime.nullable(),
  disabledAt: isoTime.nullable(),
});

/** Значения для окна SmartPSS «База данных посещаемости (сторонняя)» (FR-020). Пароля здесь нет. */
export const SmartpssValues = S({
  dbType: z.literal('MySQL'),
  serverIp: Ipv4,
  port: z.number().int().min(1).max(65535),
  dbName: z.string(),
  user: z.string(),
  /** Пароль можно показать (`GET …/secret`): шаг 4 ещё не выполнен. */
  passwordAvailable: z.boolean(),
});

/**
 * VPN площадки (шаг 3): адрес, можно ли скачать файл подключения, есть ли сервер AmneziaWG (research R6). В
 * `SiteDetail.vpn = null` — на сервере нет VPN: способ «VPN» не предлагается.
 */
export const SiteVpn = S({ address: Ipv4.nullable(), configAvailable: z.boolean(), amnezia: z.boolean() });

/**
 * Прежняя учётная запись «Сахалинской» (`smartpss_customer`, research R17, T049): можно ли её отключить
 * (`POST …/retire-legacy`) и почему нельзя — кодом: `no_session` — нет сессии новой учётной записи, `observing` — не
 * прошли 3 суток после её первой сессии (`until`), `no_address` — адрес компьютера площадки неизвестен, `pending` —
 * заявка выполняется, `retired` — уже отключена.
 */
export const SiteLegacy = S({
  canRetire: z.boolean(),
  reason: z.enum(['no_session', 'observing', 'no_address', 'pending', 'retired']).nullable(),
  until: isoTime.nullable(),
});
export type SiteLegacy = z.infer<typeof SiteLegacy>;

/** Образец для копирования правил: только код и название, без данных исходной площадки. */
export const SiteRuleSource = S({ code: SiteCode, name: z.string().min(1) });
export type SiteRuleSource = z.infer<typeof SiteRuleSource>;

/**
 * Правила к запуску (FR-004a, поправка 20.10.2026): образец, кто ведёт правила площадки, черновик к запуску (число
 * изменённых правил и ведущий). `sourceChanged` — образец опубликовал новую версию после начала черновика.
 */
export const SiteRulesPrep = S({
  source: SiteCode,
  sourceName: z.string().min(1),
  admins: z.array(S({ userId: id, name: z.string() })),
  draft: S({ changes: count, leaderName: z.string().nullable() }).nullable(),
  sourceChanged: z.boolean(),
});
export type SiteRulesPrep = z.infer<typeof SiteRulesPrep>;

/**
 * Кто может вести правила площадки (`GET …/rule-people`, FR-027): действующий пользователь сервиса; `current` — уже
 * администратор правил площадки, `sourceAdmin` — администратор правил образца (выбор по умолчанию).
 */
export const SiteRulePerson = S({
  userId: id,
  name: z.string(),
  current: z.boolean(),
  sourceAdmin: z.boolean(),
});
export type SiteRulePerson = z.infer<typeof SiteRulePerson>;

/** Страница площадки (`GET /api/sites/:site`, ответ `POST /api/sites` и `PUT …/link`). */
export const SiteDetail = S({
  site: SiteInfo,
  revision,
  steps: z.array(SiteStep).length(10),
  smartpss: SmartpssValues,
  vpn: SiteVpn.nullable(),
  terminals: z.array(TerminalRow),
  /**
   * Последняя отметка площадки — для плашки «Данные СКУД» в шапке её страницы (FR-053): по всем отметкам площадки, не
   * только за окно терминалов шага 5. Отметок нет — `null`.
   */
  lastPassAt: isoTime.nullable(),
  observation: Observation.nullable(),
  uploads: z.array(SiteUpload),
  /** Открытые тревоги. */
  alerts: z.array(SiteAlert),
  /** Прежняя учётная запись — только у первой площадки; у остальных `null`. */
  legacy: SiteLegacy.nullable().optional(),
  /** Правила к запуску (FR-004a): `null` — образец не выбран или площадка запущена. */
  rulesPrep: SiteRulesPrep.nullable().optional(),
});
export type SiteDetail = z.infer<typeof SiteDetail>;

/** Пароль SmartPSS (`GET …/secret`): только пока шаг 4 не выполнен, потом 410 `SECRET_EXPIRED`. */
export const SiteSecret = S({ password: z.string() });

/**
 * Что остаётся после отключения площадки (`GET …/consequences`, FR-062, R20): числа для окна «Отключить площадку» —
 * отметки, люди и табели площадки. Окно запрашивает их при открытии; в `SiteDetail` их нет (страница опрашивается).
 */
export const SiteConsequences = S({ passes: count, people: count, timesheets: count });
export type SiteConsequences = z.infer<typeof SiteConsequences>;

/** «Сведения для поддержки» (`GET …/support`): текст для копирования без ФИО, адресов людей и секретов. */
export const SiteSupport = S({ text: z.string() });

// ── Тела запросов ──

/** Шаг 1 (`POST /api/sites`): город из списка или «Другой город» с поясом (`CITY_UNKNOWN`, research R15). */
export const SiteCreate = S({
  name: text(1, 60),
  city: text(1, 60),
  tzOffset: TzOffset.optional(),
  code: SiteCode.optional(),
});
export type SiteCreate = z.infer<typeof SiteCreate>;

/**
 * Ответы шагов (`PUT …/answers`): шаги 2 и 7 (contracts/http-api.md) и шаг 3 — «статус «Активен», а сигнала нет»
 * (research R19, `VPN_BLOCKED`).
 */
export const SiteAnswers = z.discriminatedUnion('step', [
  S({ revision, step: z.literal(2), answers: Step2Answers }),
  S({ revision, step: z.literal(3), answers: Step3Answers }),
  S({ revision, step: z.literal(5), answers: Step5Answers }),
  S({ revision, step: z.literal(7), answers: Step7Answers }),
]);
export type SiteAnswers = z.infer<typeof SiteAnswers>;

/** Шаг 3 и смена способа связи (`PUT …/link`). */
export const SiteLinkPut = S({ revision, mode: LinkMode });

/** Новый пароль SmartPSS или новый ключ VPN (`POST …/reissue`): старый перестаёт работать. */
export const SiteReissue = S({ revision, kind: z.enum(['password', 'vpn_key']) });

/** «Разрешить адрес» (`POST …/allow-address`): адрес из последних отказов снимка, иначе `ADDRESS_NOT_SEEN`. */
export const AllowAddress = S({ revision, address: Ipv4 });

/** Адрес терминала в пути `PUT …/terminals/:ip`. */
export const TerminalIp = Ipv4;
/** Направление терминала (`PUT …/terminals/:ip`): новая строка с датой «сегодня» по поясу площадки. */
export const TerminalDirectionPut = S({ revision, direction: Direction });

/**
 * Шаг 6 (`POST …/control-pass`): «Прошёл вход» или «Прошёл выход» и время нажатия; нет времени — время сервера.
 * `reset` — «Начать заново»: нажатия стираются; `skip` — явно пропустить необязательную проверку (contracts/http-api.md).
 */
export const ControlPass = S({ kind: z.enum(['in', 'out', 'reset', 'skip']), pressedAt: isoTime.optional() });

/**
 * Шаг 7 вручную (`POST …/extract-confirm`, FR-024, уточнение 26.10.2026): только после двух утр без строк выгрузки
 * (`EXTRACT_WAIT` с `manual: true`), иначе `EXTRACT_CONFIRM_NOT_ALLOWED`.
 */
export const ExtractConfirm = S({ revision });

/** «Продолжить с риском» (`POST …/risk`, FR-015): только у шага с `warn`, причина 5–500 знаков. */
export const RiskAccept = S({ revision, step: StepNo, reason: text(5, 500) });

/** Кто ведёт правила площадки (FR-027): номера пользователей, не больше 50. */
const RulesAdmins = z.array(id).max(50);

/**
 * Шаг 10 (`POST …/launch`, FR-027): дата начала табелей, площадка-образец правил (FR-004, research R14) и кто ведёт
 * правила площадки — роль даётся тем, у кого её нет (поправка 20.10.2026).
 */
export const SiteLaunch = S({
  revision,
  startDate: isoDate,
  rulesSource: SiteCode,
  rulesAdmins: RulesAdmins.optional(),
});
export type SiteLaunch = z.infer<typeof SiteLaunch>;

/** «Подготовить правила» (`PUT …/rules-prep`, FR-004a): образец и хотя бы один человек, который ведёт правила. */
export const SiteRulesPrepPut = S({ revision, rulesSource: SiteCode, rulesAdmins: RulesAdmins.min(1) });
export type SiteRulesPrepPut = z.infer<typeof SiteRulesPrepPut>;

/** Отключение (`POST …/disable`, FR-062): название должно совпасть, иначе `CONFIRM_NAME_MISMATCH`. */
export const SiteDisable = S({ revision, confirmName: text(1, 60) });
/** Отключить прежнюю учётную запись «Сахалинской» (`POST …/retire-legacy`, R17): с вводом названия, как отключение. */
export const SiteRetireLegacy = SiteDisable;

/** Получатели тревог (`PUT …/alert-recipients`, FR-052). */
export const AlertRecipientsPut = S({ recipients: z.array(AlertRecipient).max(50) });

/** Выпустить строки карантина в обработку (`POST …/quarantine/release`). */
export const QuarantineRelease = S({ ids: z.array(id).min(1).max(1000), reason: text(5, 500) });
